import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

const PAD = 8;

function useTargetRect(selector) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!selector) { setRect(null); return; }
    let scrolled = false;
    const tick = () => {
      const el = document.querySelector(selector);
      if (!el) { setRect(null); return; }
      if (!scrolled) { el.scrollIntoView({ block: "center", behavior: "smooth" }); scrolled = true; }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
    };
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [selector]);
  return rect;
}

function Blockers({ rect }) {
  const base = "fixed z-[200] bg-black/60";
  if (!rect) return <div className={`${base} inset-0`} data-testid="tutorial-blocker" />;
  const { top, left, width, height } = rect;
  return (
    <>
      <div className={base} style={{ top: 0, left: 0, right: 0, height: Math.max(0, top) }} />
      <div className={base} style={{ top: top + height, left: 0, right: 0, bottom: 0 }} />
      <div className={base} style={{ top, left: 0, width: Math.max(0, left), height }} />
      <div className={base} style={{ top, left: left + width, right: 0, height }} />
      <div className="fixed z-[201] pointer-events-none rounded-2xl tut-ring" data-testid="tutorial-highlight" style={{ top, left, width, height }} />
      <div className="fixed z-[202] pointer-events-none tut-arrow text-5xl text-[#FFE600]" data-testid="tutorial-arrow"
           style={{ top: top - 58, left: left + width / 2 - 18 }}>▼</div>
    </>
  );
}

// steps: [{ text, target?, onEnter? }]. exitAfter: slide Ricky out after the last line, then onFinish.
// noBlock: no dark overlay (page stays clickable). stay: keep the last line on screen until unmounted.
export function RickyTutorial({ steps, exitAfter, onFinish, holdTarget, noBlock, stay, side, compact }) {
  const [i, setI] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const step = steps[i];
  const target = step ? step.target : holdTarget;
  const rect = useTargetRect(target);
  const pinned = stay && i === steps.length - 1;

  useEffect(() => { step?.onEnter?.(); }, [i]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!step || pinned) return;
    const t = setTimeout(() => next(), 2500 + step.text.length * 55);
    return () => clearTimeout(t);
  }, [i]); // eslint-disable-line react-hooks/exhaustive-deps

  const next = () => {
    if (i + 1 < steps.length) return setI(i + 1);
    if (stay) return;
    setI(steps.length);
    if (exitAfter) { setLeaving(true); setTimeout(() => onFinish?.(), 900); }
    else onFinish?.();
  };

  const right = side ? side === "right" : rect && rect.left + rect.width / 2 < window.innerWidth / 2;
  const centered = side === "center" || (!side && !rect && window.innerWidth < 640);
  const bubbleW = compact && centered ? "min(300px, 34vw)" : null;
  return (
    <>
      {!leaving && !noBlock && <Blockers rect={rect} />}
      <div className={`fixed bottom-0 inset-x-0 px-2 sm:px-6 z-[210] pointer-events-none flex ${side === "center" ? "justify-center" : side ? (right ? "justify-end" : "justify-start") : !rect ? "justify-center" : right ? "justify-end" : "justify-start"}`}>
      <div className={`relative ${leaving ? "tut-out" : "tut-in"}`} data-testid="ricky-tutorial">
        {step && (
          <div key={i} onClick={next} data-testid="tutorial-bubble"
               className={`pointer-events-auto cursor-pointer absolute bottom-[88%] ${bubbleW ? "left-1/2" : centered ? "left-1/2 -ml-[130px]" : right ? "right-[55%]" : "left-[55%]"} ${bubbleW ? "" : "w-[260px]"} ${compact ? "sm:w-[300px] sm:text-sm" : "sm:w-[360px] sm:text-base"} bg-white text-[#0A051A] font-heading uppercase tracking-wide rounded-2xl pl-4 pr-9 py-3 border-2 border-[#0A051A] shadow-[0_4px_0_#0A051A] text-xs leading-tight`}
               style={{ animation: "bulk-bubble-pop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)", ...(bubbleW ? { width: bubbleW, marginLeft: `calc(${bubbleW} / -2)` } : {}) }}>
            {step.text}
            {!pinned && <button data-testid="tutorial-skip-btn" aria-label="Skip" onClick={(e) => { e.stopPropagation(); next(); }}
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-[#0A051A] text-white flex items-center justify-center hover:bg-[#FF007F] transition-colors">
              <X size={14} strokeWidth={3} />
            </button>}
            <span className={`absolute -bottom-3 ${right ? "right-6" : "left-6"} w-0 h-0`} style={{ borderLeft: "10px solid transparent", borderRight: "10px solid transparent", borderTop: "12px solid #FFFFFF" }} />
          </div>
        )}
        <img src="/rickyflash_character.webp" alt="Ricky Flash" className={`${right ? "-scale-x-100" : ""} ${compact ? (side === "center" ? "w-[clamp(110px,15vw,210px)] max-h-[28vh] object-contain object-bottom" : "w-[150px] sm:w-[200px] lg:w-[240px] max-h-[38vh] object-contain object-bottom") : "w-[210px] sm:w-[330px]"} h-auto drop-shadow-[0_0_20px_#FF007F88]`} />
      </div>
      </div>
      <style>{`
        @keyframes bulk-bubble-pop { 0% { opacity: 0; transform: scale(0.4) rotate(-6deg); } 70% { opacity: 1; transform: scale(1.08) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
        @keyframes tut-in { from { transform: translateY(110%); } to { transform: translateY(0); } }
        @keyframes tut-out { to { transform: translateY(120%); } }
        @keyframes tut-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(12px); } }
        @keyframes tut-glow { 0%,100% { box-shadow: 0 0 0 3px #FFE600, 0 0 24px #FFE600; } 50% { box-shadow: 0 0 0 5px #FFE600, 0 0 44px #FFE600; } }
        .tut-in { animation: tut-in 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
        .tut-out { animation: tut-out 0.8s ease-in forwards; }
        .tut-arrow { animation: tut-bob 0.8s ease-in-out infinite; text-shadow: 0 0 14px #FFE600, 2px 2px 0 #000; }
        .tut-ring { animation: tut-glow 1s ease-in-out infinite; }
      `}</style>
    </>
  );
}
